import React from 'react';
import styled from 'styled-components';
import './ScaleInput.scss';

/**
 * Colour needs the styled-components theme — Sass has no token access here
 * (see MEMORY: "Sass cannot reach colour tokens"). Layout lives in
 * ScaleInput.scss.
 *
 * `background.secondary` is an alpha tint (`rgba(13, 3, 0, 0.05)`), not a
 * fixed hex, so it darkens whatever surface sits behind it — the modal
 * card's own background — rather than hard-coding the design's literal
 * `--color-surface-nested` value. Over the card's surface white it lands on
 * that token's intended shade on both channels, and it stays correct if the
 * card's surface ever changes.
 */
const Track = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const Circle = styled.label<{ checked: boolean; diameter: number }>`
  width: ${({ diameter }) => diameter}px;
  height: ${({ diameter }) => diameter}px;
  /*
   * The selected circle carries the channel's action colour, so it reads as
   * the same "this is chosen/actionable" colour as the primary button:
   * near-black on Unmind, purple on Telus. colors.primary is not that
   * colour — it is blue700 on Unmind — so the button theme is the only
   * multi-channel source for it.
   */
  background-color: ${({ theme, checked }) =>
    checked ? theme.button.primary.default.backgroundColor : 'transparent'};
  /*
   * No resting border — the design's circles are borderless, with only a
   * hover ring. box-sizing: border-box is set globally (App/GlobalStyle),
   * so this 0-width border doesn't change the box: the diameters above are
   * unaffected by dropping it.
   */
  border: solid 0 transparent;
  color: ${({ theme, checked }) =>
    checked ? theme.colors.text.inverse : theme.colors.text.primary};
  transition:
    background 0.15s,
    color 0.15s;

  &:hover,
  input:focus-visible + & {
    box-shadow: ${({ theme }) =>
      `inset 0 0 0 2px ${theme.colors.text.primary}`};
  }
`;

export type ScaleInputProps = {
  questionId: string;
  /** The question text, announced with the group. */
  legend: string;
  /**
   * Id of an element (typically the visible question heading) that already
   * announces the question text. When given, the fieldset is labelled by
   * that element via `aria-labelledby` instead of rendering its own visually
   * hidden `<legend>`, so the question is announced once rather than twice.
   * Falls back to the hidden `<legend>` when omitted, so this stays
   * optional for any consumer that doesn't already render that heading.
   */
  labelledBy?: string;
  minimumValue: number;
  maximumValue: number;
  /**
   * Gap between consecutive options, from the question's `scale.stepSize`.
   * Every question ships 1 today, but the value is server-configured, so the
   * track is generated from it rather than assuming consecutive integers.
   * Falls back to 1 for a non-positive value, which would otherwise loop.
   */
  stepSize?: number;
  minimumLabel: string | null;
  maximumLabel: string | null;
  value: number | null;
  onChange(value: number): void;
};

/**
 * The pill track of numbered circles, used for both the 0-5 WHO-5 items and
 * the 0-10 presenteeism question.
 *
 * Mirrors `Shared/Radio`'s visual language (filled circle, primary when
 * selected) but keeps native `<input type="radio">` elements rather than
 * reusing `Shared/Radio`'s button-based control: that control has no arrow
 * key handling of its own (verified — no keydown/roving-focus code anywhere
 * under `Shared/Radio` or its consumers), so its `role="radio"` buttons rely
 * entirely on the browser's native radiogroup behaviour for arrow-key
 * navigation, which browsers only supply for real `<input type="radio">`
 * elements, not for `role="radio"` buttons. Reusing it here would have
 * silently dropped the roving focus this survey currently gets for free.
 * Each input is visually hidden and its label styled as the circle, so
 * focus, roving arrow-key navigation and announcement all still come from
 * the browser.
 */
const ScaleInput = ({
  questionId,
  legend,
  labelledBy,
  minimumValue,
  maximumValue,
  stepSize = 1,
  minimumLabel,
  maximumLabel,
  value,
  onChange,
}: ScaleInputProps) => {
  const step = stepSize > 0 ? stepSize : 1;
  const values = Array.from(
    { length: Math.floor((maximumValue - minimumValue) / step) + 1 },
    (_, index) => minimumValue + index * step,
  );

  // Mirrors the prototype's `(q.max || 5) > 5 ? 36 : 44`: the 0-10
  // presenteeism scale (11 options) gets the smaller circle so all of them
  // fit the track with visible gaps; the 0-5 WHO-5 scale (6 options) gets
  // the larger one.
  const circleDiameter = values.length > 6 ? 36 : 44;

  return (
    <fieldset className="wellbeing-measure-scale" aria-labelledby={labelledBy}>
      {labelledBy ? null : (
        <legend className="wellbeing-measure-scale__legend">
          <span className="wellbeing-measure-scale__legend-text">{legend}</span>
        </legend>
      )}
      <Track className="wellbeing-measure-scale__track">
        {values.map(option => (
          <span key={option} className="wellbeing-measure-scale__option">
            <input
              className="wellbeing-measure-scale__input"
              id={`${questionId}-${option}`}
              type="radio"
              name={questionId}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              // A native radio fires no `change` event when the option that
              // is already checked is clicked again, so re-picking the
              // current answer would report nothing. On an auto-advancing
              // question that leaves a member who stepped back with no way
              // forward but to choose a different number. `click` does fire
              // in that case, so the two handlers divide the work: `change`
              // for a genuine change, this for a re-selection. The guard is
              // what keeps a normal selection from reporting twice — on
              // that path `value` is still the previous answer.
              onClick={() => {
                if (value === option) {
                  onChange(option);
                }
              }}
            />
            <Circle
              className="wellbeing-measure-scale__circle"
              htmlFor={`${questionId}-${option}`}
              checked={value === option}
              diameter={circleDiameter}
            >
              {option}
            </Circle>
          </span>
        ))}
      </Track>
      <div className="wellbeing-measure-scale__anchors">
        <span className="label-s">{minimumLabel}</span>
        <span className="label-s">{maximumLabel}</span>
      </div>
    </fieldset>
  );
};

export default ScaleInput;
